import { Modal, Tabs, Input, Button, Checkbox, Row, Col, Dropdown, Skeleton, Empty, message } from "antd";
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { TabsProps } from "antd";
import { DownOutlined, SearchOutlined, FilterOutlined, TeamOutlined } from "@ant-design/icons";
import { getChannelConnections, ChannelConnectionItem } from "../../api/ChannelConnection";
import { getChannelGroups, getChannelGroupById, updateChannelGroup } from "../../api/ChannelGroup";
import { CHANNEL_TYPE_LABELS, getChannelImage } from "./channelTypes";
import { removeVietnameseTones } from "../../lib/utils";

interface Props {
    open: boolean;
    onClose: () => void;
}

interface GroupState {
    id: number;
    name: string;
    selectedIds: number[];
    loaded: boolean;
}

const ConnectGroupModal = ({ open, onClose }: Props) => {
    const { t } = useTranslation();

    const [groups, setGroups] = useState<GroupState[]>([]);
    const [connections, setConnections] = useState<ChannelConnectionItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [activeKey, setActiveKey] = useState("");
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [editingValue, setEditingValue] = useState("");
    const [searchText, setSearchText] = useState("");
    const [platformKey, setPlatformKey] = useState("all");

    // Build danh sách nền tảng từ chính channel_type_code có trong dữ liệu
    // (giống cách trang cha build tabs) — không hardcode để tránh lệch code
    const platformItems = [
        {
            key: "all",
            label: t("chats.tab_all"),
            icon: <FilterOutlined className="text-gray-600" />,
        },
        ...Array.from(new Set(connections.map((c) => c.channel_type_code))).map((code) => ({
            key: code,
            label: CHANNEL_TYPE_LABELS[code] || code,
            icon: (
                <img
                    src={getChannelImage(code)}
                    className="w-4 h-4"
                    alt={code}
                />
            ),
        })),
    ];

    const currentGroup = groups.find((g) => String(g.id) === activeKey);
    const hasSelected = !!currentGroup && currentGroup.selectedIds.length > 1;

    // ================= LOAD DETAIL (danh sách kênh đã gộp của nhóm) =================
    const loadGroupDetail = useCallback(async (groupId: number) => {
        const res = await getChannelGroupById(groupId);
        if (res.status && res.data) {
            const ids = (res.data.connections || []).map((c) => c.id);
            setGroups((prev) =>
                prev.map((g) =>
                    g.id === groupId ? { ...g, selectedIds: ids, loaded: true } : g
                )
            );
        }
    }, []);

    // ================= LOAD DATA KHI MỞ MODAL =================
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setSearchText("");
        setPlatformKey("all");

        const fetchData = async () => {
            setLoading(true);
            const [groupRes, connRes] = await Promise.all([
                getChannelGroups({ limit: 100 }),
                getChannelConnections({ page: 1, limit: 200 }),
            ]);
            if (cancelled) return;

            if (connRes.status && connRes.data) {
                setConnections(connRes.data.items);
            }

            if (groupRes.status) {
                const gs: GroupState[] = groupRes.items.map((g) => ({
                    id: g.id,
                    name: g.name,
                    selectedIds: (g.connections || []).map((c) => c.id),
                    loaded: Array.isArray(g.connections),
                }));
                setGroups(gs);
                if (gs.length > 0) {
                    setActiveKey(String(gs[0].id));
                    if (!gs[0].loaded) loadGroupDetail(gs[0].id);
                }
            }
            setLoading(false);
        };

        fetchData();
        return () => { cancelled = true; };
    }, [open, loadGroupDetail]);

    const handleTabChange = (key: string) => {
        setActiveKey(key);
        const g = groups.find((x) => String(x.id) === key);
        if (g && !g.loaded) loadGroupDetail(g.id);
    };

    // ================= LƯU NHÓM (ghi đè toàn bộ danh sách ID đã chọn) =================
    const [submitting, setSubmitting] = useState(false);

    const handleEnterMergeMode = async () => {
        const group = currentGroup;
        if (!group || !group.loaded || submitting) return;

        setSubmitting(true);
        const res = await updateChannelGroup(group.id, {
            channel_connection_ids: group.selectedIds,
        });
        setSubmitting(false);

        if (res.status) {
            message.success(t("chats.update_success"));
            onClose();
        } else {
            message.error(t("chats.update_group_failed"));
        }
    };

    // ================= RENAME =================
    const startEdit = (group: GroupState) => {
        setEditingKey(String(group.id));
        setEditingValue(group.name);
    };

    const saveEdit = () => {
        if (!editingKey) return;
        const group = groups.find((g) => String(g.id) === editingKey);
        setEditingKey(null);
        if (!group || !editingValue.trim() || editingValue === group.name) return;

        const prevName = group.name;
        const newName = editingValue.trim();
        setGroups((prev) =>
            prev.map((g) => (g.id === group.id ? { ...g, name: newName } : g))
        );
        // Chỉ đổi tên: không truyền channel_connection_ids để giữ nguyên danh sách kênh
        updateChannelGroup(group.id, { name: newName }).then((res) => {
            if (!res.status) {
                message.error(t("chats.update_group_failed"));
                setGroups((prev) =>
                    prev.map((g) => (g.id === group.id ? { ...g, name: prevName } : g))
                );
            }
        });
    };

    // ================= LỌC KÊNH THEO SEARCH + NỀN TẢNG =================
    const filteredConnections = connections.filter((conn) => {
        // Tìm kiếm không phân biệt dấu tiếng Việt ("vien tham my" khớp "Viện Thẩm Mỹ")
        const keyword = removeVietnameseTones(searchText.trim());
        const matchSearch =
            !keyword ||
            removeVietnameseTones(conn.display_name || "").includes(keyword) ||
            conn.external_id?.includes(keyword);
        const matchPlatform =
            platformKey === "all" ||
            conn.channel_type_code === platformKey;
        return matchSearch && matchPlatform;
    });

    // ================= CHỌN KÊNH =================
    const toggleApp = (connId: number) => {
        const group = currentGroup;
        if (!group || !group.loaded) return;

        const exists = group.selectedIds.includes(connId);
        const newIds = exists
            ? group.selectedIds.filter((id) => id !== connId)
            : [...group.selectedIds, connId];

        setGroups((prev) =>
            prev.map((g) => (g.id === group.id ? { ...g, selectedIds: newIds } : g))
        );
    };

    const toggleSelectAll = () => {
        const group = currentGroup;
        if (!group || !group.loaded || filteredConnections.length === 0) return;

        const allSelected = filteredConnections.every((c) =>
            group.selectedIds.includes(c.id)
        );
        const newIds = allSelected
            ? group.selectedIds.filter(
                (id) => !filteredConnections.some((c) => c.id === id)
            )
            : Array.from(
                new Set([...group.selectedIds, ...filteredConnections.map((c) => c.id)])
            );

        setGroups((prev) =>
            prev.map((g) => (g.id === group.id ? { ...g, selectedIds: newIds } : g))
        );
    };

    const isAllFilteredSelected =
        !!currentGroup &&
        filteredConnections.length > 0 &&
        filteredConnections.every((c) => currentGroup.selectedIds.includes(c.id));

    // ================= TABS =================
    const items: TabsProps["items"] = groups.map((group) => ({
        key: String(group.id),
        label:
            editingKey === String(group.id) ? (
                <Input
                    size="small"
                    autoFocus
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onBlur={saveEdit}
                    onPressEnter={saveEdit}
                    onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === "Escape") setEditingKey(null);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: 120 }}
                />
            ) : (
                <div onDoubleClick={() => startEdit(group)}>
                    {group.name}
                </div>
            ),
        children: (
            <div>
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4">
                    <Button
                        size="small"
                        onClick={toggleSelectAll}
                        disabled={!group.loaded || filteredConnections.length === 0}
                        style={{ padding: "8px 12px", display: "flex", alignItems: "center", gap: 4, height: 32 }}
                    >
                        {isAllFilteredSelected
                            ? t("chats.deselect_all")
                            : t("chats.select_all")}
                    </Button>

                    <div className="flex gap-2">
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder={t('chats.search_page')}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            allowClear
                            className="w-full md:w-[220px]"
                        />

                        <Dropdown
                            menu={{
                                items: platformItems,
                                selectedKeys: [platformKey],
                                onClick: (e) => setPlatformKey(e.key),
                            }}
                        >
                            <Button className="w-[32px] h-[32px] p-0 flex items-center justify-center border border-gray-200 hover:border-[#3e91ab]">
                                {platformKey === "all" ? (
                                    <FilterOutlined />
                                ) : (
                                    <img
                                        src={getChannelImage(platformKey)}
                                        className="w-4 h-4"
                                        alt={platformKey}
                                    />
                                )}
                            </Button>
                        </Dropdown>
                    </div>
                </div>

                {loading || !group.loaded ? (
                    <Row gutter={[16, 16]}>
                        {Array.from({ length: 4 }).map((_, idx) => (
                            <Col key={`skeleton-${idx}`} xs={24} sm={12} md={12} lg={8}>
                                <div className="border border-gray-200 rounded-lg p-3">
                                    <div className="flex items-center gap-3">
                                        <Skeleton.Avatar active size={64} shape="circle" />
                                        <div className="flex-1">
                                            <Skeleton active title={false} paragraph={{ rows: 2, width: ['80%', '60%'] }} />
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                ) : filteredConnections.length === 0 ? (
                    <Empty description={t("chats.no_page")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <Row gutter={[16, 16]}>
                        {filteredConnections.map((conn) => {
                            const checked = group.selectedIds.includes(conn.id);
                            const channelImg = getChannelImage(conn.channel_type_code);

                            return (
                                <Col
                                    key={conn.id}
                                    xs={24}   // mobile
                                    sm={12}   // tablet nhỏ
                                    md={12}   // tablet
                                    lg={8}    // desktop
                                >
                                    <div
                                        onClick={() => toggleApp(conn.id)}
                                        style={{
                                            border: checked
                                                ? "1px solid #3e91ab"
                                                : "1px solid #e5e7eb",
                                            background: checked ? "#f9f4d9" : "#fff",
                                            borderRadius: 8,
                                            padding: "8px 12px",
                                            cursor: "pointer",
                                            transition: "all 0.2s",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                            }}
                                        >
                                            {/* Left side */}
                                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                                <div className="w-16 h-16 bg-gray-200 flex-shrink-0">
                                                    <img
                                                        src={conn.avatar_url || channelImg}
                                                        alt={conn.display_name}
                                                        className="rounded-md object-contain w-full h-full"
                                                        onError={(e) => {
                                                            const target = e.currentTarget;
                                                            if (target.src !== channelImg) {
                                                                target.src = channelImg;
                                                            }
                                                        }}
                                                    />
                                                </div>

                                                <div>
                                                    <div style={{ fontWeight: 500, marginBottom: 2 }}>
                                                        {conn.display_name}
                                                    </div>

                                                    <div className="flex gap-1 items-center text-[12px] text-gray-500">
                                                        <img
                                                            src={channelImg}
                                                            alt={conn.channel_type_code}
                                                            className="w-[14px] h-[14px] rounded-sm object-cover"
                                                        />
                                                        {conn.external_id}
                                                    </div>

                                                    <div className="flex items-center gap-1 text-[12px] mt-1 text-[#22c55e]">
                                                        <TeamOutlined />
                                                        <span className="font-medium">
                                                            {(conn.total_channel_customers || 0).toLocaleString('vi-VN')}
                                                        </span>
                                                        <span className="text-gray-400">{t('chats.customers')}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Checkbox */}
                                            <Checkbox
                                                className="
                                                    [&_.ant-checkbox-checked_.ant-checkbox-inner]:bg-[#3e91ab]
                                                    [&_.ant-checkbox-checked_.ant-checkbox-inner]:border-[#3e91ab]
                                                "
                                                checked={checked}
                                                onChange={() => toggleApp(conn.id)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>
                )}
            </div>
        ),
    }));

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={window.innerWidth < 768 ? "95%" : 926}
            title={t('chats.merge_mode')}
        >
            {loading && groups.length === 0 ? (
                <div className="mt-4">
                    <Skeleton active paragraph={{ rows: 4 }} />
                </div>
            ) : (
                <Tabs
                    type="card"
                    activeKey={activeKey}
                    onChange={handleTabChange}
                    items={items}
                    moreIcon={<DownOutlined />}
                    className="mt-4"
                />
            )}

            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 24,
                }}
            >
                <div />
                <Button
                    type="primary"
                    className="h-[32px] bg-[#3e91ab] border-[#3e91ab] hover:!bg-[#357f96] hover:!border-[#357f96]"
                    disabled={!hasSelected}
                    loading={submitting}
                    onClick={handleEnterMergeMode}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256" className="icon-btn-merge"><path d="M120,80A40,40,0,1,1,80,40,40,40,0,0,1,120,80Zm56,40a40,40,0,1,0-40-40A40,40,0,0,0,176,120ZM80,136a40,40,0,1,0,40,40A40,40,0,0,0,80,136Zm128,32H184V144a8,8,0,0,0-16,0v24H144a8,8,0,0,0,0,16h24v24a8,8,0,0,0,16,0V184h24a8,8,0,0,0,0-16Z"></path></svg>
                    {t('chats.enter_merge_mode')}
                </Button>
            </div>
        </Modal>
    );
};

export default ConnectGroupModal;
